import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, HelpCircle, Briefcase, Globe, Award } from "lucide-react";
import Spinner from "../common/Spinner";
import articleService from "../../services/articleService";
import { secureLocalStorage } from "../../utils/storageUtil.js";

/**
 * Feature text MUST match requirements exactly.
 */
const PLAN_FEATURES = {
  free: [
    "Limited Content Access",
    "Access to 3-5 journal articles per Year",
    "Read abstracts of all published articles",
    "Access to featured/highlighted articles only",
  ],
  learning: [
    "Unlimited access to all published AJMM journal articles",
    "PDF downloads with watermarks",
    "Monthly newsletter with clinical pearls",
    "Access to archive (2025 onwards)",
    "10% discount on conference tickets",
  ],
  publishing: [
    "Submit and publish research with priority",
    "Priority editorial handling (Fast-track)",
    "Exclusive author benefits & profile highlight",
    "All Learning Access features included",
    "Waiver of 2 Article Processing Charges (APC)",
    "Watermark-free PDF downloads",
  ],
};

const Plan = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await articleService.getPlans();
      if (result?.success) {
        setPlans(result?.data?.data?.plan || []);
      } else {
        setError("Failed to fetch plans");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch plans. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribeChange = async (id) => {
    const storedUser = secureLocalStorage.getItem("userData", true);
    if (!storedUser) {
      navigate("/login");
      return;
    }

    try {
      const result = await articleService.createPayment(id);
      const url = result?.data?.data;
      if (url) window.open(url, "_parent");
      else setError("Failed to start payment.");
    } catch (err) {
      console.error(err);
      setError("Unable to start payment.");
    }
  };

  /* -------- Helpers -------- */

  const getPlanKey = (plan) => {
    const amount = Number(plan?.amount ?? 0);
    const name = (plan?.name || "").toLowerCase();
    const type = (plan?.type || "").toLowerCase();

    if (amount === 0 || name.includes("free")) return "free";
    if (type.includes("submit") || name.includes("publish")) return "publishing";
    return "learning";
  };

  const getRecommended = (plan) => {
    const amount = Number(plan?.amount ?? 0);
    const type = (plan?.type || "").toLowerCase();
    const name = (plan?.name || "").toLowerCase();
    return amount === 1800 || type.includes("submit") || name.includes("publish");
  };

  const formatPrice = (plan) => {
    const p = plan?.amount ?? plan?.price ?? plan?.planPrice;
    return Number(p) === 0 ? "$0" : `$${p}`;
  };

  /* -------- Render -------- */

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Access & Publishing Plans
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Choose a plan that fits your reading and publishing needs. 
          </p>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size="medium" />
          </div>
        ) : error ? (
          <div className="text-center text-red-600">{error}</div>
        ) : (
          <>
            {/* 🔥 RESPONSIVE GRID */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
              {plans.map((plan) => {
                const planKey = getPlanKey(plan);
                const features = PLAN_FEATURES[planKey];
                const recommended = getRecommended(plan);
                const isFree = planKey === "free";

                return (
                  <div
                    key={plan._id}
                   className={`relative flex flex-col bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden border ${
                      recommended
                        ? "border-medical-500 ring-4 ring-medical-500/10"
                        : "border-gray-200 dark:border-slate-700"
                    } hover:shadow-2xl transition-all duration-300`}
                  >
                    {recommended && (
                      <div className="bg-medical-600 text-white text-center py-2 text-sm font-bold uppercase tracking-wide">
                        Recommended
                      </div>
                    )}

                    <div className="p-8 flex-grow">
                      <h3 className="font-serif text-3xl font-bold text-gray-900 dark:text-white">
                        {plan.name}
                      </h3>

                      <div className="mt-4 flex items-baseline">
                        <span className="text-5xl font-extrabold text-gray-900 dark:text-white">
                          {formatPrice(plan)}
                        </span>
                        {!isFree && (
                          <span className="ml-2 text-lg text-gray-500 dark:text-gray-400">
                            / year
                          </span>
                        )}
                      </div>

                      <p className="mt-4 text-gray-600 dark:text-gray-400">
                        {plan.description}
                      </p>

                      <hr className="my-8 border-gray-200 dark:border-slate-700" />

                      <ul className="space-y-4">
                        {features.map((f, i) => (
                          <li key={i} className="flex items-start">
                            <Check className="w-5 h-5 text-medical-600 mt-0.5" />
                            <span className="ml-3 text-gray-700 dark:text-gray-200">
                              {f}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-8 pt-0">
                      {isFree ? (
                        <button
                          disabled
                          className="w-full py-4 rounded-xl font-bold bg-gray-100 dark:bg-slate-700 text-gray-500 cursor-not-allowed"
                        >
                          Get Started for Free
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSubscribeChange(plan._id)}
                          className={`w-full py-4 rounded-xl font-bold transition-all
                            ${
                              recommended
                                ? "bg-medical-600 text-white hover:bg-medical-700"
                                : "bg-medical-50 dark:bg-slate-700 text-medical-700 dark:text-white hover:bg-medical-100 dark:hover:bg-slate-600"
                            }`}
                        >
                          Subscribe Now
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Plan;
